// Outbound calling: pure helpers (no React), so Jest loads them without the
// components barrel. The desk choice, how a dial/claim response is read, and
// which pill controls a call gets.

// "Which desk is this computer?" is per computer (the desk phone sits next to
// it), not per user, and per practice.
export const deskKey = (practiceId) => `outbound:desk:${practiceId ?? 'none'}`;

const storage = () => {
    try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; }
};

export const readDeskId = (practiceId) => {
    try {
        const v = storage()?.getItem(deskKey(practiceId));
        return v ? String(v) : null;
    } catch { return null; }
};

export const writeDeskId = (practiceId, deskId) => {
    try {
        if (deskId == null) storage()?.removeItem(deskKey(practiceId));
        else storage()?.setItem(deskKey(practiceId), String(deskId));
    } catch { /* storage unavailable */ }
};

// The last desk this browser claimed. Unlike deskKey it survives a release (sign-out releases
// the desk), so the next sign-in can suggest it (CU-869fmdxu8).
export const lastDeskKey = (practiceId) => `outbound:lastDesk:${practiceId ?? 'none'}`;

export const readLastDeskId = (practiceId) => {
    try {
        const v = storage()?.getItem(lastDeskKey(practiceId));
        return v ? String(v) : null;
    } catch { return null; }
};

export const rememberLastDesk = (practiceId, deskId) => {
    try { if (deskId != null) storage()?.setItem(lastDeskKey(practiceId), String(deskId)); } catch { /* storage unavailable */ }
};

export const CONNECTION = Object.freeze({
    CONNECTED: 'connected',
    NOT_CONNECTED: 'not_connected',
    NEEDS_RECONNECTING: 'needs_reconnecting',
});

// A number the CRM can dial: Call Logs shows withheld / odd numbers as
// "Unknown", which must never become a Call button.
export const isDiallable = (to) => typeof to === 'string' && /^\+?[\d ()-]+$/.test(to.trim()) && to.replace(/\D/g, '').length >= 6;

export const deskLabel = (desk) => (desk ? (desk.name || desk.extension || `Desk ${desk.id}`) : '');

export const connectionLabel = (desk) => {
    if (!desk) return '';
    if (desk.connection_state === CONNECTION.CONNECTED) return 'Connected';
    if (desk.connection_state === CONNECTION.NEEDS_RECONNECTING) return 'Needs reconnecting';
    return 'Not connected';
};

// "R TCO 2 · ext 205": how My desk names a desk everywhere (CU-869fmcc9r).
export const deskSummary = (desk) => (desk ? [deskLabel(desk), desk.extension && `ext ${desk.extension}`].filter(Boolean).join(' · ') : '');

// The desk list (B6 §3) decides whether the desk picker and Call buttons exist.
// No endpoint (backend without B6) or 403 (neither Call Logs nor calling) → no
// picker, no buttons. Anyone who can see Call Logs may pick a desk; `can_dial`
// says whether they may also start calls (CU-869fmcc9r). An older backend
// without `can_dial` only answered callers.
export const readStatus = (response) => {
    if (!response?.ok || !response.data || !Array.isArray(response.data.desks)) {
        return { canPickDesk: false, canCall: false, desks: [], myDeskId: null, defaultDeskId: null, deskPrompt: true };
    }
    const desks = response.data.desks.filter((d) => d && d.id != null);
    const { mine } = response.data;
    return {
        canPickDesk: true,
        canCall: response.data.can_dial !== false,
        desks,
        myDeskId: mine?.desk_id != null ? String(mine.desk_id) : null,
        // The admin-set usual desk, suggested first in the picker (CU-869fmd1vf).
        defaultDeskId: response.data.default_desk_id != null ? String(response.data.default_desk_id) : null,
        // The practice's "Ask staff which desk they're at" switch (CU-869fncprw). Only an explicit
        // false turns it off: a backend that doesn't send it yet means on, as before.
        deskPrompt: response.data.desk_prompt !== false,
    };
};

// What a claim / dial / control answer means for the UI.
export const RESULT = Object.freeze({
    OK: 'ok',
    IN_USE: 'in_use',
    MANUAL: 'manual',    // dial on the handset (no desk / not connected / needs reconnecting)
    UNKNOWN: 'unknown',  // no answer: the call may still have been placed
    ERROR: 'error',
});

const MANUAL_CODES = new Set(['no_desk', 'desk_not_connected', 'needs_reconnecting']);

// What a refused Transfer / End means, in words. The backend answers these with a bare
// `code`, and an empty message is never shown, so without these the pill said nothing
// at all (CU-869fn0jee).
export const HANDSET_NOT_READY = Object.freeze({
    desk_not_connected: 'This handset isn\'t set up for transfers yet, so the CRM can\'t move or end the call. Use the desk phone, and ask your practice admin to set the handset up in Phone system settings.',
    needs_reconnecting: 'This handset\'s sign-in has expired, so the CRM can\'t move or end the call. Use the desk phone, and ask your practice admin to reconnect it in Phone system settings.',
    no_desk: 'You don\'t share a handset the CRM can act through. Use the desk phone, and ask your practice admin to share the handsets with you.',
});

// No response, or a 5xx without a backend error code, means "outcome unknown":
// the backend may already have asked Elevate to dial, so the UI never suggests
// trying again. A 502 carrying B6's `code` is Elevate refusing the call, and
// 501 is hold/unhold not offered by Elevate (B6 §6): both are definite.
export const readResult = (response) => {
    if (!response || response.status === 0) return { kind: RESULT.UNKNOWN };
    const data = response.data || {};
    if (response.status === 501) return { kind: RESULT.ERROR, message: data.detail || 'Not available on this phone system.' };
    if (response.status === 502 && data.code) return { kind: RESULT.ERROR, message: data.detail || 'The phone system did not accept the call.' };
    if (response.status >= 500) return { kind: RESULT.UNKNOWN };
    if (response.ok && data.status === 'manual') {
        return { kind: RESULT.MANUAL, number: data.number || '', reason: data.reason || '', message: data.message || '' };
    }
    if (response.ok) return { kind: RESULT.OK, data };
    if (response.status === 409 && data.code === 'desk_in_use') return { kind: RESULT.IN_USE, heldBy: data.held_by || null };
    if (response.status === 409 && MANUAL_CODES.has(data.code)) {
        return {
            kind: RESULT.MANUAL,
            number: data.number || '',
            reason: data.code,
            message: data.message || data.detail || HANDSET_NOT_READY[data.code],
        };
    }
    if (response.status === 409 && data.code === 'call_not_active') return { kind: RESULT.ERROR, message: 'This call has already ended.' };
    if (response.status === 409 && data.code === 'not_your_call') return { kind: RESULT.ERROR, message: 'Your desk isn\'t on this call.' };
    return { kind: RESULT.ERROR, message: data.detail || 'Something went wrong. Please try again.' };
};

export const heldByName = (heldBy) => heldBy?.name || 'someone else';

// Pill controls for a call: only the user's own live call (never a DentalSEM
// staff observer copy), and only once the backend says this user can call.
// No Hold / Resume: Elevate offers no hold command, so the button only ever
// produced a red "Use the hold button on the desk phone" (CU-869fn06p7, x3).
export const controlsFor = (call) => {
    if (!call || call.closed || !call.mine || call.recipientReason === 'superuser') return [];
    if (call.state === 'on_call' || call.state === 'held') return ['transfer', 'end'];
    // My own dial while it rings: I can cancel it.
    if (call.state === 'ringing' && call.direction === 'outbound') return ['end'];
    return [];
};

export const CONTROL_LABEL = { transfer: 'Transfer', end: 'End' };

// Whether Transfer / End can work for this user at all. The backend acts through a
// handset's own Elevate sign-in -- the one this user sits at, or one shared with
// them -- so with none of those signed in every press is refused (CU-869fn0jee).
// A backend that doesn't send `shared_with_me` can't say, so the buttons stay.
export const controlReadiness = (desks, myDeskId) => {
    const list = desks || [];
    if (!list.some((d) => d && Object.prototype.hasOwnProperty.call(d, 'shared_with_me'))) return { ready: true, reason: '' };
    const mine = list.filter((d) => d.is_active !== false
        && (d.shared_with_me === true || (myDeskId != null && String(d.id) === String(myDeskId))));
    if (mine.some((d) => d.connection_state === CONNECTION.CONNECTED)) return { ready: true, reason: '' };
    if (mine.length === 0) return { ready: false, reason: HANDSET_NOT_READY.no_desk };
    if (mine.some((d) => d.connection_state === CONNECTION.NEEDS_RECONNECTING)) {
        return { ready: false, reason: HANDSET_NOT_READY.needs_reconnecting };
    }
    return { ready: false, reason: HANDSET_NOT_READY.desk_not_connected };
};

// Desks someone can transfer to: every other active, connected desk (B6
// transfers to the desk's extension).
export const transferTargets = (desks, myDeskId) => (desks || [])
    .filter((d) => String(d.id) !== String(myDeskId) && d.is_active !== false && d.connection_state === CONNECTION.CONNECTED);

// "Send it to the phone I'm at" -- the pick-up Elevate does not offer, built out of
// the transfer it does. Only for someone who holds no desk: whoever holds one is
// already at their own phone, and transferTargets already leaves it out. Choosing a
// handset here also answers "which phone are you at?", which is the desk claim nobody
// performs as a morning ritual.
export const myPhoneTargets = (desks, myDeskId) => (myDeskId != null ? [] : (desks || [])
    .filter((d) => d.is_active !== false && d.connection_state === CONNECTION.CONNECTED));

// "Anyone uses any phone": some handset is shared with this person. Nobody sits at a handset
// each morning there, so there's no morning desk prompt; Call asks instead.
export const isSharedSetup = (desks) => (desks || []).some((d) => d?.shared_with_me === true);

// Whether the once-a-day "Which desk are you at?" prompt is for this person (CU-869fncprw).
// The desk only decides which handset rings when they press Call, so only people who can press
// Call are asked; someone sharing handsets never is; the practice can switch it off. Anyone who
// presses Call without a desk is still asked then (startCall opens the picker).
export const shouldAskDesk = (status) => Boolean(
    status?.canPickDesk
    && status.canCall
    && !status.myDeskId
    && status.desks?.length
    && !isSharedSetup(status.desks)
    && status.deskPrompt !== false,
);

// held_by is someone else's (B6 uses is_me).
export const heldBySomeoneElse = (desk) => (desk?.held_by && !desk.held_by.is_me ? desk.held_by : null);

// Sign-in desk pill (CU-869fmdxu8). The desk to suggest: the admin-set usual desk, else the
// last desk this browser claimed, if it still exists.
export const suggestedDesk = (desks, { usualDeskId, lastDeskId } = {}) => {
    const find = (id) => (id != null ? (desks || []).find((d) => String(d.id) === String(id)) : null);
    return find(usualDeskId) || find(lastDeskId) || null;
};

// Desks carry no site, so the site is the branch code their names start with ("R TCO 2" → R,
// "MK Office 1" → MK). A name without one ("Reception 1") has no site, and neither does a
// role and a number ("TCO 1": the rest must contain a word).
export const deskSite = (desk) => {
    const m = /^([A-Z]{1,3})\s+(.*[A-Za-z].*)$/.exec(deskLabel(desk));
    return m ? m[1] : '';
};

const matches = (desk, q) => [deskLabel(desk), desk.extension, heldBySomeoneElse(desk)?.name]
    .some((v) => v && String(v).toLowerCase().includes(q));

// Desks matching `query` (name, extension or who holds it), grouped by site: `homeSite` first,
// then A–Z, desks without a site last. Order inside a group stays as the server sent it.
export const groupDesksBySite = (desks, { query = '', homeSite = '' } = {}) => {
    const q = query.trim().toLowerCase();
    const groups = new Map();
    (desks || []).filter((d) => !q || matches(d, q)).forEach((d) => {
        const site = deskSite(d);
        if (!groups.has(site)) groups.set(site, []);
        groups.get(site).push(d);
    });
    const rank = (site) => {
        if (homeSite && site === homeSite) return 0;
        return site ? 1 : 2;
    };
    return [...groups.entries()]
        .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
        .map(([site, list]) => ({ site, desks: list }));
};

// How a desk picker lays out the desks: `[{ key, label, desks, collapsible }]`. The list from
// voice-desks/ is already this practice's own handsets only (the server applies the same "ours"
// rule as the phone-setup page). When some are shared with me, those come first ("Your team's
// handsets") and the rest fold under "Other desks", open when a search reaches them. Otherwise
// by site, as before (CU-869fn0jf5 follow-up).
export const pickerSections = (desks, { query = '', homeSite = '' } = {}) => {
    const list = desks || [];
    if (!list.some((d) => d.shared_with_me === true)) {
        return groupDesksBySite(list, { query, homeSite }).map(({ site, desks: group }) => ({
            key: `site-${site || 'none'}`,
            label: site ? `Site ${site}${site === homeSite ? ' · your site' : ''}` : 'Other desks',
            desks: group,
            collapsible: false,
        }));
    }
    const q = query.trim().toLowerCase();
    const found = list.filter((d) => !q || matches(d, q));
    const mine = found.filter((d) => d.shared_with_me === true);
    const rest = found.filter((d) => d.shared_with_me !== true);
    return [
        mine.length && { key: 'shared', label: 'Your team\'s handsets', desks: mine, collapsible: false },
        rest.length && { key: 'other', label: 'Other desks', desks: rest, collapsible: !q },
    ].filter(Boolean);
};

export const newIdempotencyKey = () => {
    try { if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID(); } catch { /* fall through */ }
    return `dial-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};
